import Row from "../ui/Row";
import SearchBox from "../features/search/SearchBox";
import SearchList from "../features/search/SearchList";
import { useSearch } from "../features/search/useSearch";
import { useSearchParams } from "react-router-dom";
import { createContext, useEffect, useState } from "react";
import styled from "styled-components";
import Heading from "../ui/Heading";
import ButtonHelp from "../ui/ButtonHelp";
import { PermissionClaim } from "supertokens-auth-react/recipe/userroles";
import Session from "supertokens-auth-react/recipe/session";
import SearchSuggestions from "../features/search/SearchSuggestions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SidebarProvider, useSidebar } from "@/components/ui/sidebar";
import FilterSidebar from "../features/search/FilterSidebar";
import { Button } from "../components/ui/button";
import { PanelRight } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { searchTypes } from "../features/search/searchTypes";
import { useSearchStore } from "@/stores/SearchStore";

export const SearchContext = createContext();

const steps = [
  {
    target: "#search-box",
    content: "Search government contracts in plain English",
    title: "Search",
    placement: "bottom-start",
    disableBeacon: true,
  },
  {
    target: "#search-filter-button",
    content:
      "Refine your search by sorting and filtering contracts by type, department, and set-aside code",
    title: "Filter Results",
    placement: "bottom-end",
    disableBeacon: true,
  },
  {
    target: "#search-example-box",
    content:
      "Browse examples below—click, edit, and search if one looks relevant to you",
    title: "Suggestions",
    placement: "bottom-start",
    disableBeacon: true,
  },
];

function sortSearch(data, searchType, searchParams) {
  const sortBy = searchParams.get("sortBy") || "relevance-desc";
  const [field, direction] = sortBy.split("-");
  const modifier = direction === "asc" ? 1 : -1;

  if (field === "due" || field === "posted") {
    const member = searchTypes[searchType][field];
    return data?.sort((a, b) => {
      const dateA = a.metadata[member]
        ? new Date(a.metadata[member])
        : new Date(NaN);
      const dateB = b.metadata[member]
        ? new Date(b.metadata[member])
        : new Date(NaN);

      const timeA = isNaN(dateA.getTime())
        ? modifier === 1
          ? Infinity
          : -Infinity
        : dateA.getTime();
      const timeB = isNaN(dateB.getTime())
        ? modifier === 1
          ? Infinity
          : -Infinity
        : dateB.getTime();

      return (timeA - timeB) * modifier;
    });
  } else {
    // Default case that sorts by "relevance"
    return data?.sort((a, b) => (a.score - b.score) * modifier);
  }
}

const SearchRow = styled(Row)`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export function clearFilters(searchParams) {
  const paramsToKeep = ["searchType", "val", "sortBy"];

  // Create a new instance of URLSearchParams
  const newSearchParams = new URLSearchParams(searchParams);

  // Iterate through searchParams and remove the unwanted keys
  searchParams.forEach((value, key) => {
    if (!paramsToKeep.includes(key)) {
      newSearchParams.delete(key);
    }
  });

  return newSearchParams;
}

function FilterSidebarTrigger({ onClick, ...props }) {
  const { toggleSidebar } = useSidebar();

  return (
    <Button
      id="search-filter-button"
      data-sidebar="trigger"
      variant="ghost"
      size="icon"
      className="h-7 w-7"
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <PanelRight />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
}

function processSearchParams(searchParams) {
  // the search is based on the url when it changes the search gets triggered
  const searchTerm = searchParams.get("val") || "";
  const searchType = searchParams.get("searchType") || "";
  const excludeKeys = ["val", "searchType", "sortBy"];
  const searchParamsKeys = Array.from(searchParams.keys()).filter(
    (key) => !excludeKeys.includes(key)
  );
  const filters = searchParamsKeys.reduce((acc, key) => {
    acc[key] = searchParams.getAll(key);
    return acc;
  }, {});
  return { searchTerm, searchType, filters };
}

function Search() {
  const claimValue = Session.useClaimValue(PermissionClaim);
  const permissions = claimValue.value;
  const isSledPermission =
    Array.isArray(permissions) && permissions.includes("sled");

  const searchPageSearchParams = useSearchStore(
    (state) => state.searchPageSearchParams
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const { isLoading, isError, data } = useSearch(
    processSearchParams(searchParams)
  );

  // Local state for search term and search type
  const [searchTerm, setSearchTerm] = useState(searchParams.get("val") || "");
  const [searchType, setSearchType] = useState(
    searchParams.get("searchType") || "federal"
  );

  useEffect(() => {
    // Clone current searchParams for safe modifications
    let updatedSearchParams = new URLSearchParams(searchParams);

    if (Array.from(searchParams.keys()).length === 0) {
      if (searchPageSearchParams) {
        updatedSearchParams = new URLSearchParams(searchPageSearchParams);
        setSearchTerm(searchPageSearchParams.get("val") || "");
        setSearchType(searchPageSearchParams.get("searchType") || "federal");
      } else {
        // default filter values
        updatedSearchParams.set("sortBy", "relevance-desc");
        updatedSearchParams.set("searchType", "federal");
      }
    }

    if (!searchPageSearchParams) {
      // Update global storage with search params
      useSearchStore.setState({ searchPageSearchParams: updatedSearchParams });
    }

    // Update URL with the updated search params
    setSearchParams(updatedSearchParams);
  }, [searchParams, searchPageSearchParams, setSearchParams]);

  const searchResults = sortSearch(data, searchType, searchParams);

  function onSearchTypeChange(value) {
    const currentSearchType = value;
    setSearchType(currentSearchType);
    const newSearchParams = clearFilters(searchParams); // Clear filters when switching search type

    // Update searchParams after clearing filters
    newSearchParams.set("searchType", currentSearchType);
    setSearchParams(newSearchParams); // Set updated searchParams
    useSearchStore.setState({ searchPageSearchParams: newSearchParams });
  }

  return (
    <SearchContext.Provider
      value={{
        searchType,
        searchTerm,
        setSearchTerm,
        searchResults,
        isLoading,
      }}
    >
      <SidebarProvider className="-mt-48">
        <div className="flex flex-col gap-5 w-full mt-48">
          <Heading as="h1">AI-Powered Search</Heading>
          <SearchRow id="search-box" type="horizontal">
            <Select
              defaultValue="federal"
              value={searchType}
              onValueChange={onSearchTypeChange}
            >
              <SelectTrigger className="w-36 h-12 bg-primary hover:bg-primary/90 dark:bg-primary/80 dark:hover:bg-primary/90 text-md font-medium text-primary-foreground justify-center gap-2">
                <SelectValue placeholder="Select a search type" />
              </SelectTrigger>
              <SelectContent className="">
                <Tooltip>
                  <SelectItem value="federal">Federal</SelectItem>
                  <TooltipTrigger asChild>
                    <div>
                      <SelectItem value="sled" disabled={!isSledPermission}>
                        State
                      </SelectItem>
                      <SelectItem value="grant" disabled={!isSledPermission}>
                        Grants
                      </SelectItem>
                      <SelectItem value="sbir" disabled={!isSledPermission}>
                        SBIR/STTRs
                      </SelectItem>
                    </div>
                  </TooltipTrigger>

                  {!isSledPermission &&  (
                    <TooltipContent>
                      <p>Exclusive to Pro Plan</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </SelectContent>
            </Select>
            <SearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            <FilterSidebarTrigger />
          </SearchRow>

          <SearchSuggestions />

          <Row>{!isError && <SearchList />}</Row>
        </div>
        <FilterSidebar />
      </SidebarProvider>
    </SearchContext.Provider>
  );
}

export default Search;
